/* eslint-disable react-hooks/exhaustive-deps */
import { Menu, MenuProps } from 'antd'
import { useRouter } from 'next/router'
import styles from './Sidebar.module.scss'
import { helpers } from './sidebar.helpers'
import { useTranslation } from 'next-i18next'
import { useAppSelector, useAppDispatch } from 'src/app/hooks'
import React, { useEffect, useState } from 'react'
import { ThePageWrapper, PageTitle, Loader } from 'src/components'
import {
  isAdminOrDeveloperState,
  userAuthenticatedState,
  isWidgetUserState,
  isAdminUserState,
  isPremiumCalculatorUserState,
  getMessageCountSelector,
  getUserMessagesCount
} from 'src/features'

const Sidebar: React.FC<{ children: React.ReactNode }> = ({ children })  => {
  const { t } = useTranslation()
  const [openKeys, setOpenKeys] = useState([])
  const [currentActiveKey, setCurrentActiveKey] = useState<string>(null)
  const route = useRouter().route
  const user = useAppSelector(userAuthenticatedState)
  const isAdminUser = useAppSelector(isAdminUserState)
  const isWidgetUser = useAppSelector(isWidgetUserState)
  const isAdminOrDeveloper = useAppSelector(isAdminOrDeveloperState)
  const isPremiumCalculatorUser = useAppSelector(isPremiumCalculatorUserState)
  const messagesCount = useAppSelector(getMessageCountSelector)
  const sections = helpers.menuSections(
    t, isAdminOrDeveloper, isWidgetUser,
    isAdminUser, currentActiveKey,
    isPremiumCalculatorUser, messagesCount
  )
  const dispatch = useAppDispatch()

  const onOpenChange: MenuProps['onOpenChange'] = keys => {
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1)
    if (['Transactions', 'Notifications'].indexOf(latestOpenKey!) === -1) {
      setOpenKeys(keys)
    } else { setOpenKeys(latestOpenKey ? [latestOpenKey] : []) }
  }

  useEffect(() => {
    dispatch(getUserMessagesCount())
  }, [dispatch])

  useEffect(() => {
    if (route !== currentActiveKey) {
      let newKey: string = null
      if (route === '/') { newKey = '/' }
      else { newKey = route.split('/')[1] }
      setCurrentActiveKey(newKey)
      setOpenKeys(helpers.getDefaultOpenKeys(newKey))
    }
  }, [route])

  if (helpers.hideSidebar(route)) {
    return (
      <ThePageWrapper>
        {user?.id ? children : <Loader />}
      </ThePageWrapper>
    )
  }

  return (
    <ThePageWrapper>
      <div className={`${styles['content-margin']} columns`}>
        <div className={'column is-3 is-full-width-mobile'}>
          <PageTitle>
            <div className={`${styles['page-title']} is-size-3`}>
              {t('user.MyClimateTrade')}
            </div>
          </PageTitle>

          <React.StrictMode>
            {currentActiveKey && <Menu
              mode='inline'
              openKeys={openKeys}
              className={styles.menu}
              onOpenChange={onOpenChange}
              activeKey={currentActiveKey}
              defaultSelectedKeys={[currentActiveKey]}
              defaultOpenKeys={helpers.getDefaultOpenKeys(currentActiveKey)}
            >
              {sections.map((section, idx) => (
                <React.Fragment key={idx}>
                  {(section.isSubMenu && section.isVisible ? (
                    <Menu.SubMenu {...section}>
                      {section.subItems.map((subItem) => {
                        if (!subItem.isVisible) return
                        return (
                          <Menu.Item
                            key={subItem.key}
                            className={(section.hasPermision && !user?.id) && 'fade-animation'}
                          >
                            {subItem.children}
                          </Menu.Item>
                        )
                      })}
                    </Menu.SubMenu>
                  ) : (section.isVisible && !section.isSubMenu) && <>
                    <Menu.Item
                      className={(section.hasPermision && !user?.id) && 'fade-animation'}
                      {...section}
                    >
                      {section.children}
                    </Menu.Item>
                  </>)}
                </React.Fragment>
              )) }
            </Menu>}
          </React.StrictMode>
        </div>

        <div className='column is-9'>
          {user?.id ? children : <Loader />}
        </div>
      </div>
    </ThePageWrapper >
  )
}

export default Sidebar
